'use client';

import { useCurrentOrgUser } from '../../../zero/use-current-user';
import { canManageLogbook } from '../../../logbook/policy';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useAuth, useClerk } from '@clerk/nextjs';
import { Check, ChevronsUpDown, LogOut } from 'lucide-react';
import { useCallback, useLayoutEffect, useState } from 'react';
import { toast } from 'sonner';
import { cn } from '@utils/cn';
import { getMoreNavItems, isRouteItemActive } from '@utils/route-utils';
import { clearPwaSessionData } from '@utils/pwa-session-cleanup';
import { useOrganizationSwitch } from '@hooks/use-organization-switch';
import type { UserOrg } from '@utils/org-utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent
} from '@components/ui/dropdown-menu';

const ROW =
  'min-h-[var(--component-row-height)] gap-3 rounded-xl px-4 py-3 text-body font-medium whitespace-normal';

export function DockMenu({
  organization_id,
  orgs,
  className
}: {
  organization_id: string;
  orgs: UserOrg[];
  className?: string;
}) {
  const t = useTranslations();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [organizationTrigger, setOrganizationTrigger] =
    useState<HTMLDivElement | null>(null);
  const [organizationSideOffset, setOrganizationSideOffset] = useState(0);
  const { signOut } = useClerk();
  const { userId } = useAuth();
  const { switching, switchOrganization } =
    useOrganizationSwitch(organization_id);
  const activeOrg = orgs.find(org => org.organization.id === organization_id);
  const member = useCurrentOrgUser(organization_id);
  const items = getMoreNavItems(organization_id, t).filter(
    item => item.id !== 'logbook' || canManageLogbook(member?.role)
  );
  const active = items
    .filter(item => isRouteItemActive(pathname, item))
    .sort((a, b) => b.href.length - a.href.length)[0];

  const positionSubmenu = useCallback(() => {
    const trigger = organizationTrigger;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const viewport = document.documentElement.clientWidth;
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const width = Math.min(18 * rem, viewport - 40);
    const room = Math.max(rect.left - 20, viewport - rect.right - 20);
    // Radix can flip a submenu, but cannot fit it beside a full-width phone menu.
    setOrganizationSideOffset(room < width ? -trigger.offsetWidth - 8 : 0);
  }, [organizationTrigger]);

  useLayoutEffect(() => {
    const trigger = organizationTrigger;
    if (!open || !trigger) return;
    positionSubmenu();
    const observer = new ResizeObserver(positionSubmenu);
    observer.observe(trigger);
    window.addEventListener('resize', positionSubmenu);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', positionSubmenu);
    };
  }, [open, organizationTrigger, positionSubmenu]);

  async function logOut() {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await clearPwaSessionData(userId);
      await signOut({ redirectUrl: '/welcome' });
    } catch {
      toast.error(t('navigation.sign-out-error'));
      setSigningOut(false);
    }
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        aria-label={t('navigation.menu')}
        className={cn(
          className,
          (open || active) && 'bg-nav-selected text-brand-text'
        )}
      >
        <ChevronsUpDown className="mobile-dock-icon" aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="end"
        sideOffset={12}
        collisionPadding={20}
        aria-label={t('navigation.menu')}
        className="navigation-glass navigation-glass-menu w-[min(20rem,calc(100vw-40px))] max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto rounded-xl border-0 p-2 text-popover-foreground"
      >
        {orgs.length > 1 ? (
          <DropdownMenuSub
            onOpenChange={opened => {
              if (opened) positionSubmenu();
            }}
          >
            <DropdownMenuSubTrigger
              ref={setOrganizationTrigger}
              className={cn(ROW, 'break-words [&>svg:last-child]:hidden')}
              disabled={switching}
            >
              <span className="min-w-0 flex-1">
                {activeOrg?.organization.name}
              </span>
              <ChevronsUpDown className="size-5 shrink-0" aria-hidden />
            </DropdownMenuSubTrigger>
            {/* Portal avoids the blurred menu becoming the submenu's fixed-position container. */}
            <DropdownMenuPortal>
              <DropdownMenuSubContent
                sideOffset={organizationSideOffset}
                collisionPadding={20}
                className="navigation-glass navigation-glass-menu max-h-[var(--radix-dropdown-menu-content-available-height)] w-[min(18rem,calc(100vw-40px))] overflow-y-auto rounded-xl p-2"
              >
                {orgs.map(({ organization }) => (
                  <DropdownMenuItem
                    key={organization.id}
                    disabled={switching}
                    className={ROW}
                    onSelect={event => {
                      event.preventDefault();
                      void switchOrganization(organization.id).then(
                        switched => {
                          if (switched) setOpen(false);
                        }
                      );
                    }}
                  >
                    <span className="min-w-0 flex-1 break-words">
                      {organization.name}
                    </span>
                    {organization.id === organization_id ? (
                      <Check
                        className="size-5 shrink-0 text-brand-text"
                        aria-label={t('common.current')}
                      />
                    ) : null}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        ) : (
          <DropdownMenuLabel
            className={cn(ROW, 'flex items-center break-words')}
          >
            {activeOrg?.organization.name}
          </DropdownMenuLabel>
        )}
        <DropdownMenuSeparator className="mx-4 bg-divider" />
        {items.map(item => (
          <DropdownMenuItem
            key={item.id}
            asChild
            className={cn(
              ROW,
              active?.id === item.id && 'bg-nav-selected text-brand-text'
            )}
          >
            <Link
              href={item.href}
              aria-current={active?.id === item.id ? 'page' : undefined}
            >
              {item.icon ? (
                <item.icon className="size-5 shrink-0" aria-hidden />
              ) : null}
              <span>{item.label}</span>
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="mx-4 bg-divider" />
        <DropdownMenuItem
          disabled={signingOut}
          className={cn(ROW, 'text-destructive focus:text-destructive')}
          onSelect={event => {
            event.preventDefault();
            void logOut();
          }}
        >
          <LogOut className="size-5 shrink-0" aria-hidden />
          {t('navigation.log-out')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
